import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Phone, ArrowRight, Grid, Shield, Layers, Hammer, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { CONTACT_INFO, IMAGES, GALLERIES } from "../data";
import Meta from "../components/Meta";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn, fadeInSlow, slideInLeft, slideInRight, staggerContainer, staggerItem } from "../lib/animations";

export default function MurerBelaegning() {
  const [activePhotoIdx, setActivePhotoIdx] = useState(null);
  const galleryItems = GALLERIES.murerbelaegning;

  const openLightbox = (idx) => {
    setActivePhotoIdx(idx);
  };

  const closeLightbox = () => {
    setActivePhotoIdx(null);
  };

  const nextPhoto = (e) => {
    e.stopPropagation();
    setActivePhotoIdx((prev) => (prev + 1) % galleryItems.length);
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    setActivePhotoIdx((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  const servicesList = [
    {
      name: "Flisebelægning & Terrasser",
      desc: "Vi anlægger smukke terrasser, indkørsler og gangstier i alt fra klassiske herregårdssten til eksklusive granitfliser og store betonfliser. Altid med fokus på et snorlige resultat.",
    },
    {
      name: "Murerarbejde & Facader",
      desc: "Komplet murerarbejde i forbindelse med nybyg, tilbyg eller renovering. Vi udfører opmuring, pudsning af vægge, vandskuring, facadeisolering samt reparation af sætningsskader.",
    },
    {
      name: "Badeværelser & Flisearbejde",
      desc: "Drømmer du om nyt badeværelse? Vi står for alt flisearbejde og vådrumssikring med øje for detaljen, perfekte fuger og fuldstændig tæthed efter gældende vådrumsregler.",
    },
    {
      name: "Fundamenter & Støbning",
      desc: "Solidt støbearbejde er fundamentet for ethvert byggeri. Vi støber fundamenter til huse, garager, tilbygninger samt terrasser, så du er sikret en stabil og frostsikker konstruktion.",
    },
  ];

  return (
    <>
      <Meta
        title="Murerarbejde & Belægning"
        description="Professionelt murerarbejde og flisebelægning i Helsingør og Nordsjælland. Vi udfører facaderenovering, badeværelser, indkørsler og terrasser."
      />

      {/* Page Header Banner */}
      <section className="relative pt-40 pb-24 bg-zinc-950 text-white rounded-none overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.murer}
            alt="Flisebelægning og murerarbejde i Helsingør"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-zinc-950" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
            Solidt håndværk fra bunden
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light uppercase tracking-wide text-stone-50">
            Murerarbejde <span className="font-semibold text-amber-500">& Belægning</span>
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl font-light leading-relaxed text-sm sm:text-base">
            Vi skaber smukke og holdbare løsninger i sten og beton. Fra præcist murerarbejde på facaden til professionel flisebelægning, der modstår det danske klima.
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="bg-stone-50 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Detailed Copy */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-7 space-y-12"
          >
            <div className="space-y-6">
              <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-wide text-zinc-900">
                Kvalitet og præcision i murer- og belægningsarbejde
              </h2>
              <p className="text-stone-600 leading-relaxed font-light">
                Uanset om du skal have renoveret husets facade, opført en ny tilbygning, eller anlagt en smuk ny fliseterrasse eller indkørsel, leverer vi et resultat af højeste kvalitet. Vi kombinerer stolte murer- og anlægstraditioner med moderne teknikker for at sikre et æstetisk og langtidsholdbart resultat.
              </p>
              <p className="text-stone-600 leading-relaxed font-light">
                Vores arbejde tager altid udgangspunkt i en grundig rådgivning. Vi hjælper dig med at vælge de rette materialer, sten og mønstre, der passer perfekt til din boligs arkitektur og dine personlige ønsker.
              </p>
            </div>

            {/* Solid foundation info */}
            <div className="bg-white border border-stone-200 p-8 rounded-none space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-wide text-zinc-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-600" />
                <span>Vigtigheden af et korrekt underlag</span>
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed font-light">
                Hemmeligheden bag en flisebelægning, der forbliver jævn og smuk år efter år, ligger under overfladen. Vi går aldrig på kompromis med bundopbygningen. Vi udgraver til korrekt dybde, etablerer et stabilt bærelag af knust beton eller stabilgrus, og afretter med præcision, før fliserne lægges. Det sikrer optimal dræning og forhindrer sætningsskader og kørespor.
              </p>
            </div>

            {/* Services Breakdown */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold uppercase tracking-wide text-zinc-900">
                Vores primære arbejdsområder
              </h3>
              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="whileInView"
                viewport={{ once: true, margin: "-100px" }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                {servicesList.map((service, idx) => (
                  <motion.div
                    key={idx}
                    variants={staggerItem}
                    className="bg-stone-100 p-6 border border-stone-200/60 rounded-none space-y-2"
                  >
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-900">
                      {service.name}
                    </h4>
                    <p className="text-stone-600 text-[11px] leading-relaxed font-light">
                      {service.desc}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column: Sidebar & Highlights */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-5 space-y-8"
          >
            {/* Highlight Box */}
            <div className="bg-zinc-900 text-white p-8 border border-zinc-800 rounded-none space-y-6">
              <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 border-b border-zinc-800 pb-3">
                Vores murer-koncept
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Kvalitetsgaranti</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Vi anvender udelukkende godkendte kvalitetsmaterialer og følger alle gældende normer og standarder for murer- og anlægsarbejde.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <Grid className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Præcision & Æstetik</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Uanset om det er fliser eller mursten, arbejder vi med millimeterpræcision for at sikre snorlige linjer og smukke overgange.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="p-1.5 bg-white text-amber-900 rounded-none shrink-0 mt-0.5">
                    <Hammer className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Erfarne fagfolk</h4>
                    <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                      Vores team består af engagerede og faglærte håndværkere, der sætter en ære i at levere et solidt og professionelt stykke arbejde.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-amber-500 text-white p-8 rounded-none space-y-4">
              <Sparkles className="w-8 h-8 text-white" />
              <h3 className="text-lg font-bold uppercase tracking-wider">
                Få et uforpligtende tilbud
              </h3>
              <p className="text-xs text-amber-50 leading-relaxed font-light">
                Har du et murer- eller belægningsprojekt i tankerne? Ring eller skriv til os i dag. Vi kommer gerne ud, besigtiger opgaven og giver et uforpligtende tilbud.
              </p>
              <div className="pt-2 flex flex-col gap-2">
                <a
                  href={`tel:${CONTACT_INFO.phone}`}
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-900 transition-colors duration-200 py-3 px-4 justify-center"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>Ring på {CONTACT_INFO.phoneFormatted}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-transparent border border-white text-white hover:bg-white/10 transition-colors duration-200 py-3 px-4 justify-center"
                >
                  <span>Gå til kontaktformular</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-zinc-950 text-white py-24 px-6 md:px-12 border-t border-zinc-800 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
              Vores Håndværk i Billeder
            </span>
            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-wide text-stone-50">
              Galleri: <span className="font-semibold text-amber-500">Murer- & Belægningsarbejde</span>
            </h2>
            <p className="mt-4 text-stone-400 max-w-2xl font-light leading-relaxed text-sm">
              Gå på opdagelse i et udvalg af vores afsluttede murer- og belægningsprojekter i Helsingør og Nordsjælland. Hvert billede repræsenterer vores kompromisløse tilgang til kvalitet og finish.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-800">
            {galleryItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => openLightbox(idx)}
                className="group relative h-80 overflow-hidden cursor-pointer border-r border-b last:border-r-0 lg:last:border-r-0 border-zinc-800"
              >
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-xs font-bold uppercase tracking-widest text-white block">
                    {item.title}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {activePhotoIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 bg-zinc-950/95 flex items-center justify-center p-4 md:p-12"
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-stone-400 hover:text-white transition-colors duration-200 p-2"
              aria-label="Luk galleri"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              onClick={prevPhoto}
              className="absolute left-4 md:left-8 text-stone-400 hover:text-white transition-colors duration-200 p-2 bg-zinc-900/50 hover:bg-zinc-900 rounded-full"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={nextPhoto}
              className="absolute right-4 md:right-8 text-stone-400 hover:text-white transition-colors duration-200 p-2 bg-zinc-900/50 hover:bg-zinc-900 rounded-full"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl max-h-[80vh] flex flex-col items-center"
            >
              <img
                src={galleryItems[activePhotoIdx].url}
                alt={galleryItems[activePhotoIdx].title}
                className="max-w-full max-h-[70vh] object-contain border border-zinc-800"
              />
              <div className="text-center mt-4 max-w-xl">
                <h3 className="text-sm font-bold uppercase tracking-wider text-amber-500">
                  {galleryItems[activePhotoIdx].title}
                </h3>
                <p className="text-xs text-stone-300 font-light mt-1">
                  {galleryItems[activePhotoIdx].desc}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
